import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import TermFunnel from "./components/TermFunnel";

const defaults = {
  brand_name: "Family Income Guard",
  logo_url: "",
  favicon_url: "/assets/family-income-guard-icon.svg",
  hero_image_url: "/assets/family-income-guard-term-hero-v2.webp",
  supporting_image_url: "/assets/family-income-guard-future-v2.webp",
  phone: "",
};

export default function Home({ startFunnel = false }) {
  const [settings, setSettings] = useState(defaults);
  const [showFunnel, setShowFunnel] = useState(startFunnel);
  const funnelRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch("/api/site_settings.php", { headers: { Accept: "application/json" } })
      .then((r) => r.json()).then((d) => d.success && setSettings({ ...defaults, ...d.settings })).catch(() => {});
  }, []);

  useEffect(() => {
    if (!settings.favicon_url) return;
    let icon = document.querySelector("link[rel~='icon']");
    if (!icon) { icon = document.createElement("link"); icon.rel = "icon"; document.head.appendChild(icon); }
    icon.href = settings.favicon_url;
  }, [settings.favicon_url]);

  const begin = () => {
    setShowFunnel(true);
    const sessionId = window.__figAttribution?.getIds?.().session_id;
    navigate(`/apply${sessionId ? `#fig_session=${encodeURIComponent(sessionId)}` : ""}`, { replace: false });
    window.figEvents?.track("funnel_started", { funnel_variant: "default" });
    setTimeout(() => funnelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="/" aria-label="Family Income Guard home">
          {settings.logo_url ? <img src={settings.logo_url} alt={settings.brand_name} /> : <><span className="brand-mark">FIG</span><span>{settings.brand_name}</span></>}
        </a>
        <div className="header-trust"><span className="trust-dot" /> Secure, private request</div>
      </header>

      <main>
        <section className="hero" style={{ "--hero-image": `url("${settings.hero_image_url}")` }}>
          <div className="hero-overlay" />
          <div className="hero-copy">
            <div className="eyebrow">Term life insurance for growing families</div>
            <h1>Protect their tomorrow.<br/><em>Starting today.</em></h1>
            <p className="hero-lede">Explore term life coverage designed to help replace your income, protect your home, and give the people who depend on you more financial security.</p>
            <button className="primary-cta" onClick={begin}>See My Options <span>→</span></button>
            <div className="hero-notes"><span>✓ No obligation</span><span>✓ Quick qualification</span><span>✓ Licensed help</span></div>
          </div>
        </section>

        <section className="proof-strip" aria-label="Coverage benefits">
          <div><strong>10–30 Years</strong><span>Term options</span></div>
          <div><strong>Flexible</strong><span>Coverage amounts</span></div>
          <div><strong>Personal</strong><span>Licensed guidance</span></div>
          <div><strong>Protected</strong><span>Private information</span></div>
        </section>

        {showFunnel && <section className="funnel-section" ref={funnelRef}><TermFunnel /></section>}

        <section className="why-section">
          <div className="section-heading"><span>Plan with confidence</span><h2>Your income helps hold your family’s plans together.</h2><p>Term life insurance can help loved ones keep moving forward if the unexpected interrupts the future you are building.</p></div>
          <div className="benefit-grid">
            <article><b>01</b><h3>Help replace your income</h3><p>Give loved ones resources for everyday expenses, childcare, education, and future goals.</p></article>
            <article><b>02</b><h3>Protect major commitments</h3><p>Explore coverage for a mortgage, debts, and the years your family relies on your earnings.</p></article>
            <article><b>03</b><h3>Get human guidance</h3><p>A licensed insurance professional can explain your options without pressure.</p></article>
          </div>
        </section>

        <section className="story-section">
          <div className="story-image" style={{ backgroundImage: `url("${settings.supporting_image_url}")` }} />
          <div className="story-copy"><span className="eyebrow dark">Their dreams. Your protection.</span><h2>Build a financial safety net around the people who count on you.</h2><p>Planning ahead is an act of care. Family Income Guard helps you take the first step in minutes, from the privacy of home.</p><button className="secondary-cta" onClick={begin}>Start My Free Request</button></div>
        </section>

        <section className="faq-section"><div><span className="eyebrow dark">Straight answers</span><h2>What to expect</h2></div><div className="faq-list">
          <details open><summary>Is this a quote or an application?</summary><p>This is a request to review options. A licensed insurance professional will confirm availability, eligibility, rates, and policy details with you.</p></details>
          <details><summary>Will I need a medical exam?</summary><p>Some term policies require an exam while others use accelerated or simplified underwriting. Product, age, health, and coverage rules vary.</p></details>
          <details><summary>How is my information used?</summary><p>Your information is used to respond to your coverage request. Health answers remain in our private lead system and are not sent to advertising platforms.</p></details>
        </div></section>

        <section className="final-cta"><span>One thoughtful step today</span><h2>Help protect the future your family is counting on.</h2><button className="primary-cta light" onClick={begin}>See My Options <span>→</span></button></section>
      </main>

      <footer><div className="footer-brand">{settings.brand_name}</div><p>Family Income Guard connects consumers with licensed insurance professionals. It is not an insurance carrier. Product availability and eligibility vary by state and carrier.</p><div><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link><Link to="/login">Admin</Link></div><small>© {new Date().getFullYear()} Family Income Guard. All rights reserved.<span style={{ marginLeft: ".65rem" }}>Operated by <a href="https://lionpridefinancial.com" style={{ marginRight: 0, color: "inherit", textDecoration: "underline", textUnderlineOffset: "2px" }}>Lion Pride Financial</a>.</span></small></footer>
    </div>
  );
}
